import { Emitter } from 'strict-event-emitter';
import type { Hotkey } from '@/types/Hotkey.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import type { SessionId } from '@/types/Session.ts';
import type { VariableConfig } from '@/types/edit/VariableConfig.ts';

export type HotkeyPressPayload = {
    hotkey: Hotkey;
};

export type onRequestPermissionStatusResponsePayload = {
    screen: boolean;
};

export type ShowTrialInputWarningPayload = {
    inputSourceName: string | null;
    screenCaptureData: string;
};

export type ShowScreenCapturePreviewPayload = {
    screenCaptureData: string | undefined;
    screenCaptureCount: number;
};

export type SetSettingsDialogVisibilityPayload = {
    visible: boolean;
};

type SetAvailableVariablesDialogVisiblePayload = {
    visible: true;
    profileConfigId: ProfileConfig['id'];
};

type SetAvailableVariablesDialogHiddenPayload = {
    visible: false;
};

export type SetAvailableVariablesDialogVisibilityPayload =
    SetAvailableVariablesDialogVisiblePayload | SetAvailableVariablesDialogHiddenPayload;

export type SetUserMessageDialogVisibilityPayload = {
    visible: boolean;
};

export type ShowAlertDialogPayload = {
    title?: string;
    description?: string;
    actionText?: string;
    cancelText?: string;
    continueCallback?: () => void;
    cancelCallback?: () => void;
};

export type ShowInputDialogPayload = {
    title?: string;
    description?: string;
    label?: string;
    defaultValue?: string;
    submitText?: string;
    cancelText?: string;
    submitCallback?: (value: string) => void;
    cancelCallback?: () => void;
};

export type StreamStartedPayload = {
    streamId: string;
};

export type StreamContentPayload = {
    streamId: string;
    deltaContent: string;
};

export type StreamFinishedPayload = {
    streamId: string;
    fullContent: string;
};

export type VariableSetPayload = {
    sessionId: SessionId;
    key: VariableConfig['key'];
    value: VariableConfig['value'];
};

export type ScrollInDirectionPayload = {
    direction: 'up' | 'down';
};

type Events = {
    // Auth
    login: [];
    // Electron
    hotkeyPress: [HotkeyPressPayload];
    onRequestPermissionStatusResponse: [onRequestPermissionStatusResponsePayload];
    // Dialogs (old)
    showTrialInputWarning: [ShowTrialInputWarningPayload];
    showFirstTimeHideWarning: [];
    showScreenCapturePreview: [ShowScreenCapturePreviewPayload];
    // Dialogs
    setSettingsDialogVisibility: [SetSettingsDialogVisibilityPayload];
    setAvailableVariablesDialogVisibility: [SetAvailableVariablesDialogVisibilityPayload];
    setUserMessageDialogVisibility: [SetUserMessageDialogVisibilityPayload];
    // Common dialog
    showAlertDialog: [ShowAlertDialogPayload];
    showInputDialog: [ShowInputDialogPayload];
    // Stream
    streamStarted: [StreamStartedPayload];
    streamContent: [StreamContentPayload];
    streamFinished: [StreamFinishedPayload];
    // Variable
    variableSet: [VariableSetPayload];
    // Scroll
    scrollInDirection: [ScrollInDirectionPayload];
};

export const eventEmitter = new Emitter<Events>();
